import React, { useEffect, useState } from 'react'
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
    Table
} from 'reactstrap'
import Moment from 'react-moment'
import toastr from 'toastr'

import ApiService from '../../data/services/ApiService'

const RUNNING_STATUSES = ['pending', 'processing', 'downloading', 'importing']
const FINAL_STATUSES = ['completed', 'failed']

const statusLabels = {
    pending: 'Iniciando...',
    processing: 'Autenticando con el SAT...',
    downloading: 'Descargando CFDIs...',
    importing: 'Importando CFDIs...',
    completed: 'Sincronización completada',
    failed: 'Error en la sincronización'
}

const getStatusColor = (status) => {
    if (status === 'completed') return 'success'
    if (status === 'failed') return 'danger'
    if (RUNNING_STATUSES.includes(status)) return 'info'
    return 'secondary'
}

const getJobStatusLabel = (job = {}) => {
    if (job.status === 'failed') {
        return job.errorMessage || 'No pude completar la sincronización con el SAT.'
    }

    if (job.status === 'importing' && job.totalEncontrados > 0) {
        return `Importando ${job.totalEncontrados} CFDIs...`
    }

    return statusLabels[job.status] || job.status
}

const formatDate = (date) => {
    if (!date) return '—'
    return new Date(date).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    })
}

const PreviewCard = ({ preview }) => {
    if (!preview) return null

    const { fechaInicio, fechaFin, yaActualizado, esPrimerSync, ultimoSync } = preview

    if (yaActualizado) {
        return (
            <div style={{
                background: '#f0fff4',
                border: '1.5px solid #1D9E75',
                borderRadius: 6,
                padding: '14px 16px',
                marginBottom: 20
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ color: '#1D9E75', fontSize: 18 }}>✓</span>
                    <strong style={{ fontSize: 14, color: '#1D9E75' }}>Tu cuenta está sincronizada</strong>
                </div>
                {ultimoSync && (
                    <div style={{ fontSize: 13, color: '#6c757d' }}>
                        Última sincronización: <Moment format="DD/MM/YYYY">{ultimoSync}</Moment>
                    </div>
                )}
                <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>
                    Vuelve mañana para importar nuevos CFDIs.
                </div>
            </div>
        )
    }

    return (
        <div style={{
            background: '#f8f9fa',
            border: '1.5px solid #dee2e6',
            borderRadius: 6,
            padding: '14px 16px',
            marginBottom: 20
        }}>
            <strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>Período a sincronizar</strong>
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                <div>
                    <span style={{ color: '#6c757d' }}>Desde:</span>{' '}
                    <strong>{formatDate(fechaInicio)}</strong>
                    {esPrimerSync && (
                        <span style={{ color: '#6c757d' }}> (inicio del año fiscal actual)</span>
                    )}
                    {!esPrimerSync && ultimoSync && (
                        <span style={{ color: '#6c757d' }}> (día después del último sync)</span>
                    )}
                </div>
                <div>
                    <span style={{ color: '#6c757d' }}>Hasta:</span>{' '}
                    <strong>{formatDate(fechaFin)}</strong>
                    <span style={{ color: '#6c757d' }}> (hoy)</span>
                </div>
            </div>
            {esPrimerSync && (
                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 8 }}>
                    Primera sincronización — se importarán todos los CFDIs del año fiscal actual.
                </div>
            )}
            {!esPrimerSync && ultimoSync && (
                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 8 }}>
                    Última sincronización: <Moment format="DD/MM/YYYY">{ultimoSync}</Moment>
                </div>
            )}
        </div>
    )
}

const SatSync = () => {
    const [certificado, setCertificado] = useState(null)
    const [llave, setLlave] = useState(null)
    const [password, setPassword] = useState('')
    const [activeJob, setActiveJob] = useState(null)
    const [historial, setHistorial] = useState([])
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [preview, setPreview] = useState(null)

    const isRunning = activeJob && RUNNING_STATUSES.includes(activeJob.status)
    const yaActualizado = preview?.yaActualizado === true

    const loadPreview = async () => {
        try {
            const response = await ApiService.getSatSyncPreview()
            setPreview(response.data.data)
        } catch {
            // Non-fatal — preview card simply won't show
        }
    }

    const loadHistorial = async () => {
        const response = await ApiService.getSatSyncHistorial()
        setHistorial(response.data.data.jobs || [])
    }

    useEffect(() => {
        Promise.all([
            loadHistorial().catch(() => {
                toastr.error('No pude cargar el historial de sincronizaciones.')
            }),
            loadPreview()
        ])
    }, [])

    useEffect(() => {
        if (!activeJob || FINAL_STATUSES.includes(activeJob.status)) {
            return undefined
        }

        const intervalId = setInterval(async () => {
            try {
                const response = await ApiService.getSatSyncStatus(activeJob._id)
                const job = response.data.data.job
                setActiveJob(job)

                if (FINAL_STATUSES.includes(job.status)) {
                    clearInterval(intervalId)
                    loadHistorial().catch(() => {})
                    loadPreview()
                }
            } catch (err) {
                clearInterval(intervalId)
                toastr.error('No pude actualizar el estado de la sincronización.')
            }
        }, 5000)

        return () => clearInterval(intervalId)
    }, [activeJob])

    const handleSubmit = async (event) => {
        event.preventDefault()
        setError('')

        if (!certificado || !llave || !password) {
            setError('Completa certificado, llave y contraseña.')
            return
        }

        const formData = new FormData()
        formData.append('certificado', certificado)
        formData.append('llave', llave)
        formData.append('password', password)

        try {
            setSubmitting(true)
            const response = await ApiService.iniciarSatSync(formData)
            const jobId = response.data.data.jobId
            const statusResponse = await ApiService.getSatSyncStatus(jobId)
            setActiveJob(statusResponse.data.data.job)
            setPassword('')
            loadPreview()
            toastr.success('Sincronización iniciada.')
        } catch (err) {
            const message = err.response?.data?.message || 'No pude iniciar la sincronización.'
            setError(message)
            toastr.error(message)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div>
            <Card>
                <CardBody>
                    <h3 style={{ marginBottom: 4 }}>Sincronizar con el SAT</h3>
                    <p style={{ color: '#6c757d', marginBottom: 20 }}>
                        Importa las facturas que emitiste desde el portal del SAT o desde otro sistema de facturación.
                    </p>

                    <PreviewCard preview={preview} />

                    {!yaActualizado && (
                        <>
                            <Alert color="info" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                <i className="mdi mdi-lock-outline" style={{ fontSize: 20 }}></i>
                                <span>
                                    Tu e.Firma se usa únicamente para autenticarte con el SAT durante la sincronización y nunca se almacena en nuestros servidores.
                                </span>
                            </Alert>

                            {error && <Alert color="danger">{error}</Alert>}

                            <form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6} xs={12}>
                                        <FormGroup>
                                            <Label>Certificado (.cer)</Label>
                                            <Input
                                                type="file"
                                                accept=".cer"
                                                onChange={(event) => setCertificado(event.target.files?.[0] || null)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <FormGroup>
                                            <Label>Llave privada (.key)</Label>
                                            <Input
                                                type="file"
                                                accept=".key"
                                                onChange={(event) => setLlave(event.target.files?.[0] || null)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={4} xs={12}>
                                        <FormGroup>
                                            <Label>Contraseña de e.Firma</Label>
                                            <Input
                                                type="password"
                                                value={password}
                                                onChange={(event) => setPassword(event.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Button color="primary" type="submit" disabled={true}>
                                    Proximamente
                                </Button>

                                {/* <Button color="primary" type="submit" disabled={submitting || isRunning}>
                                    {submitting ? 'Iniciando...' : 'Sincronizar con el SAT'}
                                </Button> */}
                            </form>
                        </>
                    )}
                </CardBody>
            </Card>

            {activeJob && (
                <Card>
                    <CardBody>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <h5 style={{ margin: 0 }}>
                                {activeJob.status === 'completed' ? '✓ ' : ''}
                                {activeJob.status === 'failed' ? '✗ ' : ''}
                                {getJobStatusLabel(activeJob)}
                            </h5>
                            <Badge color={getStatusColor(activeJob.status)}>
                                {activeJob.status}
                            </Badge>
                        </div>

                        {activeJob.status === 'completed' && (
                            <div style={{ marginTop: 16, lineHeight: 1.8 }}>
                                <div>Se importaron {activeJob.totalImportados} CFDIs.</div>
                                <div>Se omitieron {activeJob.totalOmitidos} (ya existían en Timbrat).</div>
                                {activeJob.totalErrores > 0 && (
                                    <div>{activeJob.totalErrores} con errores.</div>
                                )}
                            </div>
                        )}

                        {activeJob.status === 'failed' && (
                            <div style={{ marginTop: 16 }}>
                                <Alert color="danger">
                                    {activeJob.errorMessage || 'No pude completar la sincronización con el SAT.'}
                                </Alert>
                                <Button color="secondary" onClick={() => setActiveJob(null)}>
                                    Intenta de nuevo
                                </Button>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}

            <Card>
                <CardBody>
                    <h5>Historial de sincronizaciones</h5>
                    <div className="table-responsive">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Rango sincronizado</th>
                                    <th>Estado</th>
                                    <th>Importados</th>
                                    <th>Omitidos</th>
                                    <th>Errores</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historial.map((job) => (
                                    <tr key={job._id}>
                                        <td><Moment format="DD/MM/YYYY HH:mm">{job.createdAt}</Moment></td>
                                        <td>
                                            <Moment format="DD/MM/YYYY">{job.fechaInicio}</Moment>
                                            {' - '}
                                            <Moment format="DD/MM/YYYY">{job.fechaFin}</Moment>
                                        </td>
                                        <td>
                                            <Badge color={getStatusColor(job.status)}>{job.status}</Badge>
                                            {job.status === 'failed' && (
                                                <div style={{ color: '#f46a6a', fontSize: 12, marginTop: 4 }}>
                                                    {job.errorMessage || 'No pude completar la sincronización con el SAT.'}
                                                </div>
                                            )}
                                        </td>
                                        <td>{job.totalImportados}</td>
                                        <td>{job.totalOmitidos}</td>
                                        <td>{job.totalErrores}</td>
                                    </tr>
                                ))}
                                {historial.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ color: '#6c757d' }}>
                                            Aún no hay sincronizaciones.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <p style={{ fontSize: 12, color: '#6c757d', marginTop: 12, marginBottom: 0 }}>
                        Timbrat sincroniza automáticamente el período correcto para evitar errores con el SAT.
                    </p>
                </CardBody>
            </Card>
        </div>
    )
}

export default SatSync
